import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { logEvent } from "@/lib/analytics";
import { PRODUCTS, getProductVariantPrice } from "@/lib/products";

const CartContext = createContext(null);
const STORAGE_KEY = "noteconsole_cart_v1";

const makeKey = (id, opts = {}) =>
  `${id}::${opts.size || ""}::${opts.cover || ""}::${opts.personalization || ""}::${opts.pageColor || ""}::${opts.color || ""}`;

const productById = new Map(PRODUCTS.map((product) => [product.id, product]));

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved
        ? JSON.parse(saved).map((item) => ({
            ...item,
            priceINR: getProductVariantPrice(productById.get(item.id), item) || item.priceINR,
          }))
        : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, opts = {}, qty = 1) => {
    const key = makeKey(product.id, opts);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          image: opts.image || product.images[0],
          priceINR: getProductVariantPrice(product, opts),
          size: opts.size || product.format?.[0] || "",
          cover: opts.cover || product.defaultOptions?.cover || product.cover?.[0] || "",
          personalization: opts.personalization || product.defaultOptions?.personalization || product.personalization?.[0] || "",
          pageColor: opts.pageColor || product.defaultOptions?.pageColor || product.pageColors?.[0] || "",
          color: opts.color || product.defaultOptions?.color || product.colors?.[0] || "",
          qty,
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
    logEvent("add_to_cart", { productId: product.id, name: product.name, priceINR: product.priceINR, qty });
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQty = (key, qty) => {
    if (qty < 1) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.priceINR * i.qty, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  const shippingThreshold = 999;
  const shipping = subtotal >= shippingThreshold || subtotal === 0 ? 0 : 79;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQty,
        clear,
        subtotal,
        shipping,
        tax,
        total,
        count,
        shippingThreshold,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
